import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="error-capture"
export default class extends Controller {
  connect() {
    this.setupErrorHandlers()
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
  }

  disconnect() {
    this.removeErrorHandlers()
  }

  setupErrorHandlers() {
    // Handle regular JavaScript errors
    this.errorHandler = (event) => {
      const errorData = {
        message: event.message,
        filename: event.filename,
        line_number: event.lineno,
        column_number: event.colno,
        stack_trace: event.error?.stack,
        user_agent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString()
      }

      // Only report errors from our application, not browser extensions or benign errors
      if (this.shouldReportError(errorData)) {
        this.reportError(errorData, 'javascript-error')
      }

      return true // Prevent default browser error handling
    }

    // Handle unhandled promise rejections
    this.rejectionHandler = (event) => {
      const errorData = {
        message: event.reason?.message || String(event.reason),
        stack_trace: event.reason?.stack,
        user_agent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString()
      }

      // Only report errors from our application
      if (this.shouldReportError(errorData)) {
        this.reportError(errorData, 'unhandled-promise-rejection')
      }

      event.preventDefault()
    }

    window.addEventListener('error', this.errorHandler)
    window.addEventListener('unhandledrejection', this.rejectionHandler)
  }

  removeErrorHandlers() {
    if (this.errorHandler) {
      window.removeEventListener('error', this.errorHandler)
    }
    if (this.rejectionHandler) {
      window.removeEventListener('unhandledrejection', this.rejectionHandler)
    }
  }

  async reportError(errorData, errorType) {
    try {
      // Filter out sensitive information
      const sanitizedData = this.sanitizeErrorData(errorData)

      const response = await fetch('/api/v1/errors/javascript', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          error: {
            ...sanitizedData,
            error_type: errorType
          }
        })
      })

      if (!response.ok && response.status !== 429) {
        console.error('Failed to report error to server:', response.status)
      }
    } catch (error) {
      // Silently fail to avoid infinite error loops
      console.error('Error reporter failed:', error)
    }
  }

  shouldReportError(errorData) {
    // Filter out browser extension errors
    if (errorData.filename) {
      const filename = errorData.filename.toLowerCase()

      // Ignore browser extension errors
      if (filename.includes('chrome-extension://') ||
          filename.includes('moz-extension://') ||
          filename.includes('safari-extension://') ||
          filename.includes('webkit-masked-url://')) {
        return false
      }
    }

    // Filter out errors from stack traces that contain extension URLs
    if (errorData.stack_trace) {
      const stackTrace = errorData.stack_trace.toLowerCase()
      if (stackTrace.includes('chrome-extension://') ||
          stackTrace.includes('moz-extension://') ||
          stackTrace.includes('safari-extension://')) {
        return false
      }
    }

    // Filter out common benign errors
    const message = errorData.message?.toLowerCase() || ''

    // ResizeObserver errors are harmless and common
    if (message.includes('resizeobserver')) {
      return false
    }

    // Generic "Script error" usually means CORS-blocked third-party script
    if (message === 'script error' || message === 'script error.') {
      return false
    }

    // Disconnected port errors from extensions
    if (message.includes('disconnected port') || message.includes('extension context invalidated')) {
      return false
    }

    // Network errors from ad blockers or failed third-party requests
    if (message.includes('failed to fetch') && !errorData.stack_trace) {
      return false
    }

    // Report all other errors
    return true
  }

  sanitizeErrorData(data) {
    // Remove potential PII from URLs and messages
    const sanitized = { ...data }

    // Redact email addresses
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g

    if (sanitized.message) {
      sanitized.message = sanitized.message.replace(emailRegex, '[EMAIL_REDACTED]')
    }

    if (sanitized.stack_trace) {
      sanitized.stack_trace = sanitized.stack_trace.replace(emailRegex, '[EMAIL_REDACTED]')
    }

    // Redact SSN patterns
    const ssnRegex = /\b\d{3}-\d{2}-\d{4}\b|\b\d{9}\b/g
    if (sanitized.message) {
      sanitized.message = sanitized.message.replace(ssnRegex, '[SSN_REDACTED]')
    }

    // Redact phone numbers
    const phoneRegex = /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/g
    if (sanitized.message) {
      sanitized.message = sanitized.message.replace(phoneRegex, '[PHONE_REDACTED]')
    }

    return sanitized
  }
}